/**
 * 分块大小策略
 * 根据文件大小动态计算最优的chunk大小
 */

export interface ChunkSizeConfig {
  chunkSize: number      // 分块大小（字节）
  totalChunks: number    // 总分块数
  strategy: string       // 使用的策略名称
}

/**
 * 动态计算chunk大小
 * 目标：在保证稳定性的前提下，减少上传次数
 */
export function calculateOptimalChunkSize(fileSize: number): ChunkSizeConfig {
  const MB = 1024 * 1024
  const fileSizeMB = fileSize / MB
  
  let chunkSize: number
  let strategy: string
  
  if (fileSizeMB < 10) {
    // 小文件（<10MB）：使用5MB块，满足S3/MinIO分块规范（非末尾分块>=5MB）
    chunkSize = 5 * MB
    strategy = 'small-file'
  } else if (fileSizeMB < 100) {
    // 中等文件（10MB-100MB）：使用5MB块，平衡稳定性和速度
    chunkSize = 5 * MB
    strategy = 'medium-file'
  } else if (fileSizeMB < 500) {
    // 大文件（100MB-500MB）：使用10MB块
    chunkSize = 10 * MB
    strategy = 'large-file'
  } else if (fileSizeMB < 1024) {
    // 超大文件（500MB-1GB）：使用20MB块
    chunkSize = 20 * MB
    strategy = 'very-large-file'
  } else {
    // 巨型文件（>1GB）：使用50MB块，大幅减少上传次数
    chunkSize = 50 * MB
    strategy = 'huge-file'
  }
  
  const totalChunks = Math.max(1, Math.ceil(fileSize / chunkSize))
  
  return {
    chunkSize,
    totalChunks,
    strategy
  }
}

export function calculateChunkSizeByCount(
  fileSize: number, 
  targetChunks: number = 100
): ChunkSizeConfig {
  const MB = 1024 * 1024
  const maxChunkSize = 100 * MB
  const minChunkSize = 5 * MB
  
  let chunkSize = Math.ceil(fileSize / targetChunks)
  chunkSize = Math.max(minChunkSize, Math.min(maxChunkSize, chunkSize))
  const totalChunks = Math.max(1, Math.ceil(fileSize / chunkSize))
  
  return {
    chunkSize,
    totalChunks,
    strategy: 'fixed-count'
  }
}

export class AdaptiveChunkStrategy {
  private currentChunkSize: number
  private uploadSpeeds: number[] = []
  private readonly maxSamples = 5
  
  constructor(initialFileSize: number) {
    const config = calculateOptimalChunkSize(initialFileSize)
    this.currentChunkSize = config.chunkSize
  }
  
  adjustChunkSize(bytesPerSecond: number): number {
    const MB = 1024 * 1024
    
    this.uploadSpeeds.push(bytesPerSecond)
    if (this.uploadSpeeds.length > this.maxSamples) {
      this.uploadSpeeds.shift()
    }
    
    const avgSpeed = this.uploadSpeeds.reduce((a, b) => a + b, 0) / this.uploadSpeeds.length
    
    if (avgSpeed < 1 * MB) {
      this.currentChunkSize = Math.max(5 * MB, this.currentChunkSize * 0.8)
    } else if (avgSpeed > 10 * MB) {
      this.currentChunkSize = Math.min(50 * MB, this.currentChunkSize * 1.2)
    }
    
    return Math.floor(this.currentChunkSize)
  }
  
  getCurrentChunkSize(): number {
    return this.currentChunkSize
  }
}

export function getRecommendedConcurrency(fileSize: number, chunkSize: number): number {
  const totalChunks = Math.ceil(fileSize / chunkSize)
  
  if (totalChunks < 5) {
    return 1
  } else if (totalChunks < 20) {
    return 2
  } else if (totalChunks < 50) {
    return 3
  } else if (totalChunks < 100) {
    return 4
  } else {
    return 5
  }
}

export function formatOptimizationStats(
  fileSize: number,
  oldChunkSize: number,
  newChunkSize: number
): string {
  const oldChunks = Math.ceil(fileSize / oldChunkSize)
  const newChunks = Math.ceil(fileSize / newChunkSize)
  const reduction = ((oldChunks - newChunks) / oldChunks * 100).toFixed(1)
  
  return `优化效果：
  - 文件大小: ${(fileSize / 1024 / 1024).toFixed(2)}MB
  - 旧方案: ${oldChunks}次上传 (${(oldChunkSize / 1024 / 1024).toFixed(1)}MB/块)
  - 新方案: ${newChunks}次上传 (${(newChunkSize / 1024 / 1024).toFixed(1)}MB/块)
  - 减少: ${reduction}% 的上传次数`
}
